import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, Bell } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DueAlertsWidget } from "@/components/transactions/DueAlertsWidget";
import { SmartAlertsWidget } from "@/components/dashboard/SmartAlertsWidget";
import type { SmartAlert } from "@/hooks/use-smart-alerts";
import { useDueAlerts } from "@/hooks/use-due-alerts";

interface DashboardAlertsBannerProps {
  alerts: SmartAlert[];
  alertsLoading: boolean;
  dismissAlert: (id: string) => void;
}

export function DashboardAlertsBanner({
  alerts,
  alertsLoading,
  dismissAlert,
}: DashboardAlertsBannerProps) {
  const { alerts: dueAlerts, loading: dueLoading } = useDueAlerts();
  const [isOpen, setIsOpen] = useState(false);

  const dueCount = dueAlerts.overdue.length + dueAlerts.dueToday.length + dueAlerts.dueSoon.length;
  const smartCount = alerts.filter(a => !a.isDismissed).length;
  const totalCount = dueCount + smartCount;
  const hasOverdue = dueAlerts.overdue.length > 0;

  if (!dueLoading && !alertsLoading && totalCount === 0) {
    return null;
  }

  if (dueLoading || alertsLoading) {
    return null;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className={`w-full justify-between px-4 py-2 h-auto ${
            hasOverdue
              ? "bg-destructive/10 hover:bg-destructive/15 text-destructive"
              : "bg-warning/10 hover:bg-warning/15 text-warning"
          }`}
        >
          <div className="flex items-center gap-2">
            {hasOverdue ? (
              <AlertTriangle className="h-4 w-4" />
            ) : (
              <Bell className="h-4 w-4" />
            )}
            <span className="text-sm font-medium">
              {totalCount} alerta{totalCount !== 1 ? "s" : ""}
            </span>
            {hasOverdue && (
              <Badge variant="destructive" className="text-xs px-1.5 py-0">
                {dueAlerts.overdue.length} atrasada{dueAlerts.overdue.length > 1 ? "s" : ""}
              </Badge>
            )}
          </div>
          {isOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4 pt-3">
        <DueAlertsWidget showToasts={false} />
        <SmartAlertsWidget
          alerts={alerts}
          isLoading={alertsLoading}
          onDismiss={dismissAlert}
        />
      </CollapsibleContent>
    </Collapsible>
  );
}
